/**
 * KAN-718: таблица писем: направление, дата, от/кому, тема, клиент, вложения.
 * Клик по строке открывает письмо; ссылка на клиента открывает его переписку.
 */
import { Paperclip } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { emailCounterpart } from '@/lib/emailFormat';
import { formatCheckedAt } from '@/lib/telegramProxyDirectory';
import type { EmailItem } from '@/types/emailMailbox';
import { EmailDirectionIcon } from './EmailDirectionIcon';

interface Props {
  emails: EmailItem[];
  onOpenEmail: (email: EmailItem) => void;
  onOpenClient: (email: EmailItem) => void;
}

export function EmailsTable({ emails, onOpenEmail, onOpenClient }: Props): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <Table aria-label="Письма">
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Дата</TableHead>
            <TableHead>От / кому</TableHead>
            <TableHead>Тема</TableHead>
            <TableHead>Клиент</TableHead>
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {emails.map((email) => (
            <TableRow
              key={email.id}
              data-testid={`email-row-${email.id}`}
              className="cursor-pointer hover:bg-gray-50"
              tabIndex={0}
              onClick={() => onOpenEmail(email)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onOpenEmail(email);
              }}
            >
              <TableCell>
                <EmailDirectionIcon direction={email.direction} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs text-gray-600">
                {formatCheckedAt(email.created_at)}
              </TableCell>
              <TableCell className="max-w-[220px] truncate text-sm">{emailCounterpart(email)}</TableCell>
              <TableCell className="max-w-[360px]">
                <div className="truncate text-sm font-medium">{email.subject || '(без темы)'}</div>
                {email.preview && <div className="truncate text-xs text-gray-500">{email.preview}</div>}
              </TableCell>
              <TableCell className="text-sm">
                {email.client_id ? (
                  <button
                    type="button"
                    className="text-blue-700 hover:underline"
                    data-testid={`email-row-client-${email.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenClient(email);
                    }}
                  >
                    {email.client_name || 'Клиент'}
                  </button>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </TableCell>
              <TableCell>
                {email.has_attachments && (
                  <Paperclip
                    className="h-4 w-4 text-gray-500"
                    aria-label="Есть вложения"
                    data-testid={`email-row-attachments-${email.id}`}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
